/**
 * Минимальный рендерер Markdown.
 *
 * Своя реализация, а не библиотека: нужен разбор заголовков, списков,
 * ссылок, цитат, кода и выделения, а ближайшая по возможностям связка
 * marked + DOMPurify весит заметно больше самого редактора и тянет
 * санитайзер, который здесь не нужен вовсе.
 *
 * Санитайзер не нужен, потому что HTML тут не пропускается, а строится:
 * весь входной текст экранируется до разбора, поэтому вставленный в
 * документ тег остаётся текстом. Ссылки дополнительно проверяются по
 * схеме, чтобы `javascript:` не превращался в исполняемый переход.
 */

/** Настройки разбора, зависящие от места показа. */
export interface MarkdownOptions {
  /**
   * Язык страницы, на которой показывается текст.
   *
   * Нужен для внутренних ссылок в статьях блога. В тексте статьи ссылку
   * пишут без языка (`/app/file-transfer`), потому что один и тот же
   * адрес встречается во всех переводах. Без подстановки языка русский
   * читатель посреди русской статьи попадал на английскую страницу
   * инструмента.
   */
  locale?: string;
  /**
   * Размеры картинок и роликов по адресу. Передаёт только блог (05.10.2026):
   * строка из одной картинки становится `<figure>` с подписью из alt, а
   * ролик (`.mp4`, постер в кавычках) — `<video>`. Размеры читаются с диска
   * при сборке, а этот модуль работает и в браузере (чат), поэтому сам их
   * не узнаёт. Без размеров картинка остаётся строчной, как раньше: без
   * width и height она дёргала бы страницу при загрузке.
   */
  media?: (src: string) => { width: number; height: number } | undefined;
}

/** Языки сайта: сверяется с i18n/routing. */
const LOCALES = ['en', 'es', 'sr', 'ru'];

/**
 * Язык по умолчанию идёт без префикса (localePrefix: 'as-needed'), и
 * добавленный к нему `/en` вызвал бы лишний редирект.
 */
const DEFAULT_LOCALE = 'en';

/** Экранирует всё, что браузер мог бы принять за разметку. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Разрешены только те схемы, по которым переход безопасен. */
function safeHref(href: string): string | null {
  const value = href.trim();
  if (/^(https?:|mailto:|#|\/)/i.test(value)) return value;
  return null;
}

/** Ссылка внутрь сайта: с неё начинается путь, но не вторая косая черта. */
function isInternal(href: string): boolean {
  return href.startsWith('/') && !href.startsWith('//');
}

/** Дописывает язык страницы к внутренней ссылке без префикса локали. */
function withLocale(href: string, locale: string | undefined): string {
  if (!locale || locale === DEFAULT_LOCALE || !isInternal(href)) return href;

  const [first] = href.split('/').slice(1);
  // Язык уже указан автором явно: не трогаем, иначе получится /ru/ru/...
  if (first && LOCALES.includes(first)) return href;

  return `/${locale}${href}`;
}

/**
 * Метка для временно вынутых кусков кода.
 *
 * Строка из символов, которые не встречаются в обычном тексте, вместо
 * управляющего символа: последний ломает подсветку в редакторах и
 * справедливо запрещён линтером внутри регулярных выражений.
 */
const CODE_MARK = '\u2063CODE';

/** Разбор того, что может встретиться внутри строки. */
function inline(text: string, options: MarkdownOptions = {}): string {
  let html = escapeHtml(text);

  // Код разбирается первым: внутри него остальная разметка не действует.
  const codes: string[] = [];
  html = html.replace(/`([^`]+)`/g, (_, code: string) => {
    codes.push(code);
    return `${CODE_MARK}${String(codes.length - 1)}${CODE_MARK}`;
  });

  // Служебные скобки формул убираются, сама формула остаётся текстом.
  //
  // Модель отвечает на арифметику в виде `\[ 2 + 2 = 4 \]`: это разметка
  // LaTeX, которую человек видит как мусор вокруг ответа. Настоящий
  // набор формул потребовал бы KaTeX — это отдельная библиотека и
  // отдельный разговор, а простые выражения читаются и без неё, если
  // убрать скобки.
  html = html
    .replace(/\\\[\s*(.+?)\s*\\\]/g, '<span class="math">$1</span>')
    .replace(/\\\(\s*(.+?)\s*\\\)/g, '<span class="math">$1</span>');

  html = html.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (whole, alt: string, src: string) => {
    const href = safeHref(src);
    return href ? `<img src="${href}" alt="${alt}" loading="lazy" />` : whole;
  });

  html = html.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (whole, label: string, target: string) => {
    const href = safeHref(target);
    if (!href) return whole;

    // Ссылка на свою же страницу открывается в текущей вкладке: новая
    // вкладка на каждый переход внутри сайта плодит копии страниц и
    // ломает кнопку «назад». Заодно к ней дописывается язык статьи.
    if (isInternal(href)) {
      return `<a href="${withLocale(href, options.locale)}">${label}</a>`;
    }

    // Внешняя ссылка открывается в новой вкладке с noopener: без него
    // открытая страница получает доступ к window.opener.
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  });

  html = html
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
    .replace(/~~([^~]+)~~/g, '<del>$1</del>');

  return html.replace(
    new RegExp(`${CODE_MARK}(\\d+)${CODE_MARK}`, 'g'),
    (_, index: string) => `<code>${codes[Number(index)] ?? ''}</code>`,
  );
}

/** Ячейки строки таблицы: внешние палки отбрасываются. */
function cells(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

/**
 * Таблица, начинающаяся с указанной строки.
 *
 * Опознаётся не по палкам в тексте, а по строке-разделителю под шапкой
 * (`|---|---|`). Иначе обычная фраза с вертикальной чертой превращалась
 * бы в таблицу из одной ячейки.
 *
 * Нужна ради ответов модели: сравнения она выдаёт таблицами постоянно, а
 * без разбора они рассыпались на отдельные абзацы с палками — читать
 * такое невозможно.
 *
 * Возвращает готовый HTML и номер строки, с которой продолжать разбор.
 */
function readTable(
  lines: string[],
  start: number,
  options: MarkdownOptions = {},
): { html: string; next: number } | null {
  const header = lines[start];
  const divider = lines[start + 1];

  if (!header?.includes('|') || !divider) return null;
  // Разделитель: только дефисы, двоеточия выравнивания и палки.
  if (!/^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/.test(divider) || !divider.includes('-')) return null;

  const head = cells(header);
  if (head.length < 2) return null;

  const rows: string[][] = [];
  let index = start + 2;
  while (index < lines.length && (lines[index] ?? '').includes('|')) {
    rows.push(cells(lines[index] ?? ''));
    index += 1;
  }

  const headHtml = head.map((cell) => `<th>${inline(cell, options)}</th>`).join('');
  const bodyHtml = rows
    .map((row) => `<tr>${row.map((cell) => `<td>${inline(cell, options)}</td>`).join('')}</tr>`)
    .join('');

  return {
    html: `<table><thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`,
    next: index,
  };
}

/**
 * Строка из одной картинки или ролика: `![подпись](/путь "постер")`.
 *
 * Только внутренние адреса: блог показывает свои файлы, а чужой адрес в
 * `<video>` означал бы загрузку с постороннего сервера у каждого читателя.
 */
function readMedia(line: string, options: MarkdownOptions): string | null {
  if (!options.media) return null;
  // Кавычки у постера любые: prettier при коммите меняет "…" на '…'.
  const m = /^!\[([^\]]*)\]\(([^)\s]+)(?:\s+(?:"([^"]*)"|'([^']*)'))?\)$/.exec(line.trim());
  if (!m?.[2]) return null;
  const src = m[2];
  if (!isInternal(src) || !/^[\w/.-]+$/.test(src)) return null;
  const razmer = options.media(src);
  if (!razmer) return null;

  const podpis = escapeHtml(m[1] ?? '');
  const w = String(razmer.width);
  const h = String(razmer.height);

  if (src.endsWith('.mp4')) {
    const poster = m[3] ?? m[4];
    const posterAttr =
      poster && isInternal(poster) && /^[\w/.-]+$/.test(poster) ? ` poster="${poster}"` : '';
    // preload="none": 4–5 МБ ролика не грузятся, пока читатель не нажал.
    return `<figure><video controls muted playsinline preload="none" width="${w}" height="${h}"${posterAttr}><source src="${src}" type="video/mp4" /></video>${podpis ? `<figcaption>${podpis}</figcaption>` : ''}</figure>`;
  }
  return `<figure><img src="${src}" alt="${podpis}" width="${w}" height="${h}" loading="lazy" decoding="async" />${podpis ? `<figcaption>${podpis}</figcaption>` : ''}</figure>`;
}

/** Переводит Markdown в безопасный HTML. */
export function renderMarkdown(source: string, options: MarkdownOptions = {}): string {
  const lines = source.split('\n');
  const out: string[] = [];

  let listType: 'ul' | 'ol' | null = null;
  let inCode = false;
  let codeLines: string[] = [];

  const closeList = () => {
    if (listType) {
      out.push(`</${listType}>`);
      listType = null;
    }
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] ?? '';

    // Блок кода: внутри него строки не разбираются вовсе.
    if (/^```/.test(line)) {
      if (inCode) {
        out.push(`<pre><code>${escapeHtml(codeLines.join('\n'))}</code></pre>`);
        codeLines = [];
        inCode = false;
      } else {
        closeList();
        inCode = true;
      }
      continue;
    }

    if (inCode) {
      codeLines.push(line);
      continue;
    }

    if (!line.trim()) {
      closeList();
      continue;
    }

    // Таблица. Распознаётся по строке-разделителю под шапкой: без неё
    // обычный текст с вертикальной чертой превратился бы в таблицу из
    // одной ячейки. Модели отвечают таблицами часто, а без разбора они
    // рассыпались на отдельные абзацы с палками.
    const table = readTable(lines, i, options);
    if (table) {
      closeList();
      out.push(table.html);
      i = table.next - 1;
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading?.[1] && heading[2] !== undefined) {
      closeList();
      const level = heading[1].length;

      /*
        Явный якорь в конце заголовка: `## Файлы cookie {#cookie}`.

        Нужен, чтобы на раздел можно было сослаться адресом вида
        `/legal/privacy#cookie`. Якорь из самого текста тут не годится:
        заголовки русские, и получилась бы либо транслитерация, либо
        процентное кодирование — ни то, ни другое не напишешь руками в
        ссылке.

        Синтаксис заимствован у расширений Markdown и знаком: то же
        самое понимают pandoc и kramdown.

        Идентификатор ограничен латиницей, цифрами, дефисом и
        подчёркиванием: пробел или кавычка в нём сломали бы атрибут.
      */
      const yakor = /^(.*?)\s*\{#([A-Za-z][\w-]*)\}\s*$/.exec(heading[2]);
      const tekst = yakor?.[1] ?? heading[2];
      const id = yakor?.[2];

      out.push(
        id === undefined
          ? `<h${String(level)}>${inline(tekst, options)}</h${String(level)}>`
          : `<h${String(level)} id="${id}">${inline(tekst, options)}</h${String(level)}>`,
      );
      continue;
    }

    if (/^(-{3,}|\*{3,})$/.test(line.trim())) {
      closeList();
      out.push('<hr />');
      continue;
    }

    const quote = /^>\s?(.*)$/.exec(line);
    if (quote?.[1] !== undefined) {
      closeList();
      out.push(`<blockquote>${inline(quote[1], options)}</blockquote>`);
      continue;
    }

    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    if (bullet?.[1] !== undefined) {
      if (listType !== 'ul') {
        closeList();
        out.push('<ul>');
        listType = 'ul';
      }
      out.push(`<li>${inline(bullet[1], options)}</li>`);
      continue;
    }

    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (numbered?.[1] !== undefined) {
      if (listType !== 'ol') {
        closeList();
        out.push('<ol>');
        listType = 'ol';
      }
      out.push(`<li>${inline(numbered[1], options)}</li>`);
      continue;
    }

    const media = readMedia(line, options);
    if (media) {
      closeList();
      out.push(media);
      continue;
    }

    closeList();
    out.push(`<p>${inline(line, options)}</p>`);
  }

  // Незакрытый блок кода в конце документа: человек ещё печатает, и
  // потерять набранное из-за отсутствующей закрывающей строки нельзя.
  if (inCode && codeLines.length > 0) {
    out.push(`<pre><code>${escapeHtml(codeLines.join('\n'))}</code></pre>`);
  }
  closeList();

  return out.join('\n');
}
